import React, { useMemo, useState } from "react";
import { Globe2, Search } from "lucide-react";
import RegionalPlanRow from "./RegionalPlanRow";
import RegionalAddonRow from "./RegionalAddonRow";

/** Admin editor for per-region list prices (plans: With Teams / No Teams; add-ons: single price). */
export default function RegionalPricesSection({ dataset, plans, adminKey, onPlanSaved, onAddonSaved }) {
  const regions = dataset?.regions || [];
  const [regionId, setRegionId] = useState(regions.find((r) => r.id !== "us")?.id || regions[0]?.id || "us");
  const [tab, setTab] = useState("plans");
  const [q, setQ] = useState("");
  const region = regions.find((r) => r.id === regionId) || { id: "us", label: "United States", currency: "USD", symbol: "$", factor: 1 };
  const needle = q.trim().toLowerCase();
  const visiblePlans = useMemo(() => plans.filter((p) => (p.price || p.regional?.[regionId]) && (!needle || p.name.toLowerCase().includes(needle) || p.short.toLowerCase().includes(needle))), [plans, regionId, needle]);
  const visibleAddons = useMemo(() => (dataset?.addons || []).filter((a) => !needle || a.name.toLowerCase().includes(needle)), [dataset, needle]);
  const filled = plans.filter((p) => p.regional?.[regionId]).length;
  const estimated = plans.filter((p) => p.regional?.[regionId]?.estimated).length;

  return (
    <section className="admin-card glass" style={{ padding: 0 }} data-testid="admin-regional">
      <div className="regional-head">
        <h2 style={{ margin: 0 }}>
          <Globe2 size={16} /> Regional prices
        </h2>
        <div className="pills compact" role="group" aria-label="Region">
          {regions.map((r) => (
            <button key={r.id} type="button" className="pill" aria-pressed={regionId === r.id} onClick={() => setRegionId(r.id)} data-testid={`regional-region-${r.id}`}>
              {r.label} <span className="count">{r.currency}</span>
            </button>
          ))}
        </div>
        <div className="seg" role="group" aria-label="Table">
          <button type="button" className={tab === "plans" ? "on" : ""} aria-pressed={tab === "plans"} onClick={() => setTab("plans")} data-testid="regional-tab-plans">
            Plans
          </button>
          <button type="button" className={tab === "addons" ? "on" : ""} aria-pressed={tab === "addons"} onClick={() => setTab("addons")} data-testid="regional-tab-addons">
            Add-ons
          </button>
        </div>
        <label className="ws-search">
          <Search size={14} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter" data-testid="regional-search" />
        </label>
      </div>
      <p className="muted small regional-note" data-testid="regional-note">
        {region.id === "us"
          ? "US list prices are the base: edit them in the Plans & pricing table above (add-on US prices below). Other regions fall back to USD x factor with an ≈ marker when no official price is stored."
          : `${region.label} (${region.currency}${region.symbol ? `, ${region.symbol}` : ""}) · factor ${region.factor} · ${filled} of ${plans.length} plans priced, ${estimated} estimated.${region.source ? ` Source: ${region.source}` : ""} Changes apply immediately to the matrix, cost calculator and lookup.`}
      </p>
      <div className="admin-table-wrap">
        {tab === "plans" && (
          <table className="mx-table admin-table" data-testid="regional-plans-table">
            <thead>
              <tr>
                <th>Plan</th>
                <th>US (Teams / No Teams)</th>
                <th>{region.symbol} With Teams</th>
                <th>{region.symbol} No Teams</th>
                <th>Estimated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {region.id === "us" && (
                <tr>
                  <td colSpan={6} className="muted small" style={{ padding: 14 }}>
                    US plan prices live in the Plans &amp; pricing table above.
                  </td>
                </tr>
              )}
              {region.id !== "us" && visiblePlans.map((plan) => <RegionalPlanRow key={`${plan.id}-${region.id}`} plan={plan} region={region} adminKey={adminKey} onSaved={onPlanSaved} />)}
            </tbody>
          </table>
        )}
        {tab === "addons" && (
          <table className="mx-table admin-table" data-testid="regional-addons-table">
            <thead>
              <tr>
                <th>Add-on</th>
                <th>US</th>
                <th>{region.symbol} Price</th>
                <th>Estimated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visibleAddons.map((a) => (
                <RegionalAddonRow key={`${a.name}-${region.id}`} addon={a} region={region} adminKey={adminKey} onSaved={onAddonSaved} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
